import React, { useEffect, useReducer, useRef, useState } from 'react';
import { nextSortDirection } from '../../utils/tableSort';
import { Helmet } from 'react-helmet-async';
import {  useLocation, useNavigate } from 'react-router-dom';
import FilterForm from '../../components/FilterForm';
import { useLabels } from '../../hooks/useLabels';
import AccountService from '../../services/account.service';
import { getError, showCurrency } from '../../utils';
import { FaPlus, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { usePermissions } from '../../hooks/usePermissions';
import Paginator from '../Paginator/Paginator';
import {
	EntityList,
	PageHead,
	ListToolbar,
	DataTable,
	Button,
	Pill,
	IdTile,
	RowActionsMenu,
	ExportModal,
} from '../../components/EntityList';
import AccountDeletionModal from '../../components/AccountDeletionModal';
import { useListView } from '../../hooks/useListView';
import { isNavGroupsEnabled } from '../../utils/featureFlags';



const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			accounts: action.payload.accounts,
			page: action.payload.page,
			pages: action.payload.pages,
			countAccounts: action.payload.countAccounts,
			pageSize: action.payload.pageSize,
			loading: false,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };

	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

function AccountList() {
	const { t } = useLabels();

	const [{ loading, error, accounts, successDelete, pageSize, countAccounts }, dispatch] =
		useReducer(reducer, {
			accounts: [],
			loading: true,
			error: '',
			pageSize: 0,
			countAccounts: 0,
		});
	const initialLoading = loading && pageSize === 0;

	const isMounted = useRef(false);
	const navigate = useNavigate();

	const { search } = useLocation();
	const sp = new URLSearchParams(search); // /search?category=Shirts
	const type = sp.get('type') || 'all';
	const accountType = sp.get('accountType') || 'all';
	const query = sp.get('query') || 'all';
	const currency = sp.get('currency') || 'all';
	const [filtered, setFiltered] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [deletingAccount, setDeletingAccount] = useState(null);
	const page = sp.get('page') || 1;
	const column = sp.get('column') || 'name';
	const direction = sp.get('direction') || 'asc';
	// Account role derived from data (?role=cliente|proveedor|colaborador|socio).
	// A filtered menu tab (?view=clientes) fixes it; otherwise, with
	// SHOW_NAV_GROUPS, the toolbar offers it as a select.
	const role = sp.get('role') || 'all';
	const { viewTitle, viewParams, viewQuerySuffix, viewUrlSuffix, viewPending } = useListView();
	const roleFixed = Boolean(viewParams.role);
	const showRoleSelect = isNavGroupsEnabled() && !roleFixed;
	const roleQuerySuffix = !roleFixed && role !== 'all' ? `&role=${encodeURIComponent(role)}` : '';
	const searchSuffix = `${roleQuerySuffix}${viewQuerySuffix}`;
	const urlSuffix = `${roleQuerySuffix}${viewUrlSuffix}`;
	const pageTitle = viewTitle || t('entity.account.plural');

	const [filterOptions, setFilterOptions] = useState({
		formType: 'Cuentas',
		accountType: accountType,
		type: type,
		currency: currency,
		query: query,
	});
	// Balance filters (unpaid credit / debt dues) — their own label keys, so a
	// preset that renames debtor/creditor to Cliente/Proveedor does not turn
	// them into role filters.
	const types = [
		{ label: 'Todas', value: 'all' },
		{ label: t('filter.account.toPay'), value: 'acreedores' },
		{ label: t('filter.account.toCollect'), value: 'deudores' }
	];
	const roleOptions = [
		{ label: t('filter.account.allRoles'), value: 'all' },
		{ label: t('entity.account.role.client.plural'), value: 'cliente' },
		{ label: t('entity.account.role.supplier.plural'), value: 'proveedor' },
		{ label: t('entity.account.role.staff.plural'), value: 'colaborador' },
		{ label: t('entity.account.role.partner.plural'), value: 'socio' },
	];

	const onPageChange = (page) => {
		navigate(getFilterUrl({ page: page }));
	};

	const handleDropdownToggle = () => {
		setIsOpen(!isOpen);
	};



	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
			formType: filterOptions.formType,
		}));
	};

	const handleConceptInputChange = (value) => {
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			accountType: value,
		}));
	};

	// filterOptions seeds from the URL on mount only, but the toolbar search and
	// the paginator navigate without updating it — and getFilterUrl() rebuilds
	// every URL out of it, so a stale value dropped the active search term.
	useEffect(() => {
		setFilterOptions({
			formType: 'Cuentas',
			accountType,
			type,
			currency,
			query,
		});
	}, [accountType, type, currency, query]);

	const handleApplyFilter = () => {
		const { type, currency, accountType } = filterOptions;
		const filter = {
			type,
			currency,
			accountType,
			page: 1,
			query,
		};
		const filterUrl = getFilterUrl(filter);
		navigate(filterUrl);
		setFiltered(true);
	};
	const getFilterUrl = (filter) => {
		const { type, query, currency, accountType } = filterOptions;
		const filterQuery = filter.query || query;
		const filterPage = filter.page || 1;
		const filterType = type || 'all';
		const filterCurrency = currency || '';
		const filterAccountType = filter.accountType || accountType;
		const filterRole = filter.role || role;

		const params = new URLSearchParams();
		params.append('page', filterPage);
		params.append('type', filterType);
		params.append('accountType', filterAccountType);
		params.append('query', filterQuery);
		params.append('currency', filterCurrency);
		// Without these the sort silently reset to the default on every filter
		// change, search and page step.
		params.append('column', column);
		params.append('direction', direction);
		if (filterRole !== 'all' && !roleFixed) params.append('role', filterRole);

		return `/AdminScreen/accounts/search?${params.toString()}${viewUrlSuffix}`;
	};

	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}
		handleApplyFilter();
	}, [filterOptions.accountType, filtered]);

	const handleClearFilter = () => {
		setFilterOptions({
			formType: 'Cuentas',
			type: 'all',
			accountType: 'all',
			currency: 'all',
			query: 'all',
		});
		setFiltered(false);
	};

	const handleSort = (sortColumn) => {
		const newSortDirection = nextSortDirection(column, direction, sortColumn);
		const newURL = `/AdminScreen/accounts/search?page=${page}&type=${type}&accountType=${accountType}&query=${query}&currency=${currency}&column=${sortColumn}&direction=${newSortDirection}${urlSuffix}`;
		navigate(newURL);
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await AccountService.search(
					`page=${page}&type=${type}&accountType=${accountType}&query=${query}&currency=${currency}&column=${column}&direction=${direction}${searchSuffix}`,
				);
				dispatch({ type: 'FETCH_SUCCESS', payload: data });
			} catch (error) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			}
		};
		if (successDelete) {
			dispatch({ type: 'DELETE_RESET' });
		} else if (!viewPending) {
			fetchData();
		}
	}, [successDelete, type, column, direction, query, accountType, currency, page, searchSuffix, viewPending]);

	async function editAccountHandler(account) {
		navigate(`/AdminScreen/accounts/editAccount/${account._id}`);
	}
	async function seeDetails(account) {
		navigate(`/AdminScreen/accounts/account/${account._id}`);
	}
	async function newAccountHandler() {
		navigate('/AdminScreen/newAccount');
	}

	// Superset of the pre-refactor `accountColumns` (utils.js) and the fields
	// the current export already emitted — nothing from either gets dropped.
	const exportFieldGetters = {
		name: (account) => account.name,
		rut: (account) => account.rut || account.nroDocumento || '',
		type: (account) => account.type || '',
		currency: (account) => (account.currency ? showCurrency(account.currency) : ''),
		folder: (account) => account.folder || '',
		balance: (account) =>
			(account.balances || [])
				.filter(({ net }) => net !== 0)
				.map(({ currency, net }) => `${showCurrency(currency)} ${net}`)
				.join(' / '),
	};

	const exportFields = [
		{ key: 'name', label: t('field.name'), locked: true },
		{ key: 'rut', label: t('field.rut') },
		{ key: 'type', label: t('field.type') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'folder', label: t('field.folder') },
		{ key: 'balance', label: t('field.balance') },
	];

	async function handleExportAccounts(selectedKeys) {
		const { data } = await AccountService.search(
			`page=1&type=${type}&accountType=${accountType}&query=${query}&currency=${currency}&column=${column}&direction=${direction}&pageSize=${countAccounts || 1}${searchSuffix}`,
		);
		// Column order follows the catalog, not click order.
		const orderedKeys = exportFields.map((f) => f.key).filter((key) => selectedKeys.includes(key));
		const rows = data.accounts.map((account) =>
			Object.fromEntries(
				orderedKeys.map((key) => [exportFields.find((f) => f.key === key).label, exportFieldGetters[key](account)]),
			),
		);
		const ws = XLSX.utils.json_to_sheet(rows);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Cuentas');
		const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
		const blob = new Blob([buffer], { type: 'application/octet-stream' });
		return { blob, filename: `cuentas_${new Date().toISOString().slice(0, 10)}.xlsx` };
	}

	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: () => handleClearFilter('Cuentas'),
		handleApplyFilter: () => handleApplyFilter('Cuentas')
	};

	const { canSee, canEdit, canCreate, canDelete } = usePermissions('accounts');

	const renderRowActions = (account) => (
		<RowActionsMenu
			items={[
				canSee() && { label: 'Ver detalles', onClick: () => seeDetails(account) },
				canEdit() && { label: 'Editar', onClick: () => editAccountHandler(account) },
				canDelete() && { label: 'Eliminar', onClick: () => setDeletingAccount(account), danger: true },
			]}
		/>
	);

	const accountTableColumns = [
		{
			key: 'name',
			header: t('field.name'),
			sortable: true,
			render: (account) => (
				<div className='tw:flex tw:items-center tw:gap-2.5'>
					<IdTile name={account.name} />
					<div>
						<div className='tw:font-medium tw:text-ink-0'>{account.name}</div>
						{account.folder && account.folder !== account.name && (
							<div className='tw:text-[11.5px] tw:text-ink-3'>{account.folder}</div>
						)}
					</div>
				</div>
			),
		},
		{
			key: 'rut',
			header: 'RUT',
			render: (account) => {
				const documentId = account.rut || account.nroDocumento;
				return documentId ? (
					<span className='tw:font-mono tw:text-[13.5px] tw:text-ink-2'>{documentId}</span>
				) : (
					<span className='tw:text-ink-3'>—</span>
				);
			},
		},
		{
			key: 'contact',
			header: 'Contacto',
			render: (account) => {
				const contactName = account.mainContact && account.mainContact.name;
				const displayName = contactName || account.name;
				const firstName = displayName ? displayName.trim().split(/\s+/)[0] : '—';
				return (
					<div className='tw:flex tw:items-center tw:gap-2'>
						<IdTile name={displayName} size={22} shape='circle' />
						<span className='tw:text-[12.5px] tw:text-ink-2'>{firstName}</span>
					</div>
				);
			},
		},
		{
			key: 'type',
			header: t('field.type'),
			render: (account) => (
				<Pill tone={account.type === 'Empresa' ? 'accent' : 'neutral'}>{account.type || '—'}</Pill>
			),
		},
		{
			key: 'currency',
			header: t('field.currency'),
			className: 'tw:text-right',
			render: (account) =>
				account.currency ? (
					<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{showCurrency(account.currency)}</span>
				) : (
					<span className='tw:text-ink-3'>—</span>
				),
		},
		{
			key: 'balance',
			header: 'Saldo',
			className: 'tw:text-right',
			render: (account) => {
				// Zero-net currencies (fully paid) render as the em dash too — never "0".
				const balances = (account.balances || []).filter(({ net }) => net !== 0);
				if (balances.length === 0) {
					return <span className='tw:text-ink-3'>—</span>;
				}
				return (
					<div>
						{balances.map(({ currency, net }) => (
							<div
								key={currency}
								className={`tw:font-mono tw:text-[13px] tw:tabular-nums ${net < 0 ? 'tw:text-danger' : 'tw:text-ink-0'}`}
							>
								{net < 0 ? '−' : '+'} {showCurrency(currency)} {Math.abs(net).toLocaleString('es-UY')}
							</div>
						))}
					</div>
				);
			},
		},
	];

	return (
		<div>
			<Helmet>
				<title>{pageTitle}</title>
			</Helmet>

			<FilterForm {...filterFormProps} />

			<EntityList>
				<PageHead
					title={pageTitle}
					subtitle={`${initialLoading ? '—' : countAccounts} ${pageTitle.toLowerCase()} encontradas`}
					actions={
						<>
							<Button variant='default' icon={<FaFileExcel />} onClick={() => setExportModalOpen(true)}>
								Exportar
							</Button>
							{canCreate() && (
								<Button variant='primary' icon={<FaPlus />} onClick={newAccountHandler}>
									{`Agregar ${t('entity.account').toLowerCase()}`}
								</Button>
							)}
						</>
					}
				/>
				<ListToolbar
					query={query}
					getFilterUrl={getFilterUrl}
					navigate={navigate}
					searchPlaceholder='Buscar por nombre, tipo o carpeta…'
					segments={types}
					activeSegment={filterOptions.accountType}
					onSegmentChange={handleConceptInputChange}
					onAdvancedFilter={handleDropdownToggle}
					resultCount={initialLoading ? null : countAccounts}
				>
					{showRoleSelect && (
						<select
							aria-label='Rol'
							value={role}
							onChange={(e) => navigate(getFilterUrl({ role: e.target.value, page: 1 }))}
							className='tw:h-[34px] tw:px-2.5 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface tw:text-[12.5px] tw:text-ink-1'
						>
							{roleOptions.map((option) => (
								<option key={option.value} value={option.value}>{option.label}</option>
							))}
						</select>
					)}
				</ListToolbar>
				<DataTable
					columns={accountTableColumns}
					rows={accounts}
					loading={loading}
					skeleton
					skeletonRows={pageSize || 15} // 15 matches routes/accountRoutes.js DEFAULT_PAGE_SIZE, used before the first page load reports its own pageSize
					error={error}
					sortColumn={column}
					direction={direction}
					onSort={handleSort}
					onRowClick={(account) => seeDetails(account)}
					// Chevron column only earns its keep when it offers something the row
					// click doesn't already do (seeDetails) — mirrors DynamicListTableNew's
					// shouldShowActionColumn().
					rowActions={canEdit() || canDelete() ? renderRowActions : undefined}
					emptyMessage='No se encontraron cuentas con los filtros aplicados.'
				/>
				{pageSize > 0 && (
					<Paginator
						className='tw:mt-4'
						totalCount={countAccounts}
						pageSize={pageSize}
						currentPage={parseInt(page)}
						onPageChange={onPageChange}
					/>
				)}
			</EntityList>

			<ExportModal
				show={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				count={countAccounts}
				fields={exportFields}
				subtitle={`${pageTitle} — se exportará el listado filtrado actual`}
				onExport={handleExportAccounts}
			/>

			<AccountDeletionModal
				show={Boolean(deletingAccount)}
				account={deletingAccount}
				onClose={() => setDeletingAccount(null)}
				onDeleted={() => {
					// successDelete is the effect dependency that refetches the list.
					dispatch({ type: 'DELETE_SUCCESS' });
					setDeletingAccount(null);
				}}
			/>
		</div>
	);
}

export default AccountList;
